import Vue from 'vue';
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  MutationAction
} from 'vuex-module-decorators';

import API, { Types } from '../../modules/API';
import Utils from '../../modules/Utils';
import vuex from '..';
import { i18n } from '@/main';
import { cloneDeep } from 'lodash';

export interface Group {
  id: string;
  name: string;
  description: string;
  total_devices: number;
}

@Module({ namespaced: true })
export default class Groups extends VuexModule {
  list: Group[] = [];
  cachedGroups: { [id: string]: any } = {};

  @Mutation
  cacheGroup(group) {
    if (group && group.id) Vue.set(this.cachedGroups, group.id, group);
  }

  @Action 
  async load(data: any = {}) {
    // `load` only appends groups, so the previous project's groups have to be
    // cleared on every fresh load (`next` means we're paging the current one),
    // including 'allProjects' which returns early without fetching anything.
    if (!data.next) vuex.commit('Groups/flush');
    if (data.project_id === 'allProjects') return;
    const params = {
      p1: `${Types.PROJECTS}`,
      p2: 'groups/',
      p3: {},
      p4: { itemID: data.project_id }
    };

    if (data.next) {
      const url = data.next.split('v4/') && data.next.split('v4/')[1] && data.next.split('v4/')[1].split('/?page') && data.next.split('v4/')[1].split('/?page')[0];
      params.p1 = '';
      params.p2 = url;
      params.p3['page'] = data.next.split('/?page=') && data.next.split('/?page=')[1];
      delete params.p4.itemID;
    }

    if (data.next && (!params.p2 || !params.p3['page'])) return;

    return API.get(params.p1, params.p2, params.p3, params.p4, 'v4').then((res) => {
      if (res) {
        if (res.results) {
          res.results.forEach((group) => vuex.commit('Groups/add', group));
        }
        if (res.next && res.next !== data.next) {
          vuex.dispatch('Groups/load', {project_id: data.project_id, next: res.next});
        }
      }
    });
  }

  @Action({ commit: 'add' })
  async create({ project_id, data }) {
    return API.post('', `${Types.PROJECTS}/${project_id}/groups/`, data, {}, 'v4')
      .then((res) => {
        vuex.dispatch('Global/throwNotify', {
          type: 'success',
          title: `${i18n.t('Success')!}`,
          text: i18n.t(`Group successfully created`)
        });
        vuex.commit('Groups/add_start', {id: res.id, name: res.name, description: res.description, total_devices: res.devices.length});

        return res;
      }).catch((e) => {
        vuex.dispatch('Global/throwNotify', {
          type: 'error',
          title: `${i18n.t('Error')!}`,
          text: i18n.t(e.message || `Couldn't create group`)
        });
      });
  }

  @MutationAction({ mutate: ['list'] }) 
  async edit({ group_id, project_id, data, callback = ({ error }) => {}}) {
    // @ts-ignore
    const list = this.state.list, index = Utils.find(list, group_id);
    if (!group_id || index === -1) return;

    return API.put('', `${Types.PROJECTS}/${project_id}/groups/` + group_id, data, {}, 'v4')
      .then((res) => {
        if (res.hasOwnProperty('id') && res.hasOwnProperty('devices')) {
          vuex.dispatch('Global/throwNotify', {
            type: 'success',
            title: `${i18n.t('Success')!}`,
            text: i18n.t(`Group successfully updated`)
          });

          vuex.dispatch('Schedules/runSchedulerInServer');

          Vue.set(list, index, { ...res, total_devices: res.devices.length });
          vuex.commit('Groups/cacheGroup', res);
          callback({ error: false });
          return { list };
        } else {
          vuex.dispatch('Global/throwNotify', {
            type: 'error',
            title: `${i18n.t('Error')!}`,
            text: i18n.t(`Couldn't update group`)
          });
          callback({ error: true });
          return { list };
        }
      })
      .catch(() => {
        vuex.dispatch('Global/throwNotify', {
          type: 'error',
          title: `${i18n.t('Error')!}`,
          text: i18n.t(`Couldn't update group`)
        });
        callback({ error: true });
        return { list };
      });
  }

  @MutationAction({ mutate: ['list'] })
  async remove(data) {
    // @ts-ignore
    const list = this.state.list,
      index = Utils.find(list, data.groupID);
    if (!data.groupID || index === -1) return;

    return API.delete(
      Types.PROJECTS,
      `${data.projectID}/groups/` + data.groupID + `/`,
      null,
      {},
      'v4'
    )
      .then(() => {
        vuex.dispatch('Global/throwNotify', {
          type: 'success',
          title: `${i18n.t('Success')!}`,
          text: i18n.t(`Group successfully deleted`)
        });

        list.splice(index, 1);
        return { list };
      })
      .catch(() => {
        vuex.dispatch('Global/throwNotify', {
          type: 'error',
          title: `${i18n.t('Error')!}`,
          text: i18n.t(`Couldn't delete group`)
        });

        return { list };
      });
  }

  @Mutation flush() {
    this.list = [];
    this.cachedGroups = {};
  }

  @Mutation
  addGroupsList(groups_list){
    if (!groups_list) return;

    const new_list = groups_list.filter((new_group) => new_group.id && !this.list.find((group) => group.id === new_group.id));
    this.list = cloneDeep(this.list).concat(new_list);
  }

  @Mutation add(data: Group) {
    if (!data || !data.id || this.list.find((group) => group.id === data.id)) return;
    this.list = cloneDeep(this.list).concat([data]);
  }

  @Mutation add_start(data: Group) {
    if (!data || !data.id || Utils.find(this.list, data.id) !== -1) return;
    this.list = [data, ...this.list];
  }
}
